import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/toaster'
import { api } from '@/lib/api'
import { downloadFile } from '@/lib/download'
import { Loader2, Wand2, Image, Download, Star, RefreshCw, AlertCircle, X, Maximize2, Plus, Lightbulb, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, ArrowRightLeft, HelpCircle } from 'lucide-react'
import { ReferenceImageSelector } from '@/components/generation/ReferenceImageSelector'
import { AssetPickerModal } from '@/components/library/AssetPickerModal'
import { ImportIdeaModal } from '@/components/creative-lab/ImportIdeaModal'
import { IdeaDetailModal } from '@/components/creative-lab/IdeaDetailModal'
import { MarkdownArticle } from '@/components/creative-lab/MarkdownArticle'
import {
  GPT_IMAGE_DEFAULT_QUALITY,
  GPT_IMAGE_DEFAULT_SIZE,
  GPT_IMAGE_QUALITY_OPTIONS,
  GPT_IMAGE_SIZE_OPTIONS,
  getAspectRatioForGptImageSize,
  getDefaultGptImageSizeForAspectRatio,
} from '@/lib/gptImageSizes'
import { normalizeIdeaAdFormat } from '@/lib/ideaFormat'
import { filterValidMediaUrls } from '@/lib/utils'
import { useTasksStore } from '@/stores/tasks'
import { embedRequirementId } from '@/lib/embedContext'
import type { ToolId, Asset, Idea, IdeaInput, GenerationSlot } from '@/types'

const GEMINI_ASPECT_RATIOS = [
  { value: '1:1', label: '1:1 Square' },
  { value: '21:9', label: '21:9 Cinematic' },
  { value: '16:9', label: '16:9 Wide' },
  { value: '5:4', label: '5:4 Wide' },
  { value: '4:3', label: '4:3 Standard' },
  { value: '3:2', label: '3:2 Photo' },
  { value: '9:16', label: '9:16 Tall' },
  { value: '3:4', label: '3:4 Portrait' },
  { value: '2:3', label: '2:3 Portrait' },
  { value: '4:5', label: '4:5 Feed' },
]

// Aspect ratios limited to the overlap supported by both Gemini image models.
const ASPECT_RATIOS_BY_MODEL: Record<string, { value: string; label: string }[]> = {
  gemini_image: GEMINI_ASPECT_RATIOS,
  gemini_image_flash: GEMINI_ASPECT_RATIOS,
}

const MODELS: { value: ToolId; label: string; description: string }[] = [
  { value: 'gpt_image', label: 'GPT Image 2', description: 'High quality, high creativity' },
  { value: 'gemini_image_flash', label: 'Nano Banana 2', description: 'High quality, fast' },
]

const MAX_PRODUCT_REFERENCE_IMAGES = 5
const MAX_GENERATION_COUNT = 10

type SlotItem = { idea: Idea; ideaInput: IdeaInput } | null

function extractVerticalIdeasMetadata(product: { metadata: Record<string, unknown> | null } | null): Record<string, unknown> | null {
  if (!product?.metadata || typeof product.metadata !== 'object') {
    return null
  }
  const verticalIdeas = product.metadata.vertical_ideas
  return verticalIdeas && typeof verticalIdeas === 'object'
    ? (verticalIdeas as Record<string, unknown>)
    : null
}

function parsePrefilledCount(value: string | null): number {
  const parsed = Number.parseInt(value || '', 10)
  if (!Number.isFinite(parsed)) {
    return 1
  }
  return Math.max(1, Math.min(MAX_GENERATION_COUNT, parsed))
}

function extractAssetIdList(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return []
  }
  const items: string[] = []
  const seen = new Set<string>()
  for (const rawItem of value) {
    if (typeof rawItem !== 'string') continue
    const normalized = rawItem.trim()
    if (!normalized || seen.has(normalized)) continue
    seen.add(normalized)
    items.push(normalized)
  }
  return items
}

function createIdeaSlot(idea: Idea): NonNullable<SlotItem> {
  return {
    idea,
    ideaInput: {
      idea_id: idea.id,
      creative_reference_url: filterValidMediaUrls(idea.media_urls)[0] ?? null,
    },
  }
}

function assignImportedIdeasToSlots(
  slots: SlotItem[],
  ideas: Idea[],
  targetIndex: number | null
): SlotItem[] {
  if (ideas.length === 0) return slots

  const next = [...slots]
  const candidateIndexes =
    targetIndex !== null
      ? [
          targetIndex,
          ...next
            .map((slot, index) => ({ slot, index }))
            .filter(({ slot, index }) => index !== targetIndex && slot === null)
            .map(({ index }) => index),
        ]
      : next
          .map((slot, index) => ({ slot, index }))
          .filter(({ slot }) => slot === null)
          .map(({ index }) => index)

  let ideaIndex = 0
  for (const slotIndex of candidateIndexes) {
    if (ideaIndex >= ideas.length) break
    next[slotIndex] = createIdeaSlot(ideas[ideaIndex])
    ideaIndex += 1
  }

  return next
}

export function ImageFreestylePage() {
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const preselectedProductId = searchParams.get('product') || searchParams.get('product_id') || undefined
  const preselectedVerticalSkillKey =
    searchParams.get('vertical_skill') || searchParams.get('vertical_skill_key') || undefined
  const preselectedIdeaSource = searchParams.get('idea_source') || undefined
  const preselectedIdeaTag = searchParams.get('idea_tag') || undefined
  const preselectedCount = parsePrefilledCount(searchParams.get('count'))
  const [prompt, setPrompt] = useState('')
  const [aspectRatio, setAspectRatio] = useState('1:1')
  const [model, setModel] = useState<ToolId>('gpt_image')
  const [gptImageSize, setGptImageSize] = useState(GPT_IMAGE_DEFAULT_SIZE)
  const [gptImageQuality, setGptImageQuality] = useState(GPT_IMAGE_DEFAULT_QUALITY)
  const [count, setCount] = useState(preselectedCount)
  const [productId, setProductId] = useState<string | undefined>(preselectedProductId)
  const [verticalSkillKey, setVerticalSkillKey] = useState(preselectedVerticalSkillKey || 'none')
  const [isVerticalGuidanceOpen, setIsVerticalGuidanceOpen] = useState(false)

  // Product reference images state (shared by both modes)
  const [manualReferenceAssets, setManualReferenceAssets] = useState<Asset[]>([])
  const [includeProductScreenshot, setIncludeProductScreenshot] = useState(true)
  const [showAssetPicker, setShowAssetPicker] = useState(false)

  // Preview state
  const [previewImage, setPreviewImage] = useState<Asset | null>(null)
  const [detailIdea, setDetailIdea] = useState<Idea | null>(null)

  // Unified mode: Use Ideas toggle (default ON)
  const [useIdeas, setUseIdeas] = useState(true)

  // Slot-based state: array of length `count`
  // Each slot is either { idea, ideaInput } or null (freestyle slot)
  const [slots, setSlots] = useState<SlotItem[]>([null])

  // Slot carousel: which slot is currently visible
  const [activeSlotIndex, setActiveSlotIndex] = useState(0)

  // Modal state
  const [showImportModal, setShowImportModal] = useState(false)
  const [replacingSlotIndex, setReplacingSlotIndex] = useState<number | null>(null)

  // Use global task store
  const { tasks, addTask, updateTask, startPolling, removeTask, isPolling } = useTasksStore()

  const currentTask = Object.values(tasks)
    .filter(task => task.type === 'image_freestyle')
    .sort((a, b) => b.createdAt - a.createdAt)[0]

  const initializedSkillProductIdRef = useRef<string | null>(null)
  const autoPrefilledReferenceProductIdRef = useRef<string | null>(null)

  // Resume polling for processing tasks
  useEffect(() => {
    if (currentTask && currentTask.status === 'processing' && !currentTask.id.startsWith('pending-')) {
      if (!isPolling(currentTask.id)) {
        startPolling(currentTask.id)
      }
    }
  }, [currentTask?.id, currentTask?.status, startPolling, isPolling])

  // Refresh subscription when task completes
  useEffect(() => {
    if (currentTask?.status === 'completed' || currentTask?.status === 'failed') {
      queryClient.invalidateQueries({ queryKey: ['subscription'] })
    }
  }, [currentTask?.status, queryClient])

  // Notify hui parent frame when images are ready (once per task)
  const notifiedTaskIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (currentTask?.status !== 'completed') return
    if (notifiedTaskIdRef.current === currentTask.id) return
    const images: Asset[] = currentTask?.result || []
    if (images.length === 0 || window.self === window.top) return
    notifiedTaskIdRef.current = currentTask.id
    window.parent.postMessage({
      type: 'ASSETS_GENERATED',
      assets: images.map((img) => ({ url: img.download_url || img.url, media_type: 'image' })),
      requirement_id: embedRequirementId,
    }, '*')
  }, [currentTask?.status, currentTask?.id, currentTask?.result])

  const taskStatus = currentTask?.status || null
  const isTaskRunning = taskStatus === 'pending' || taskStatus === 'processing'
  const taskError = currentTask?.error || null
  const generatedImages: Asset[] = currentTask?.result || []

  // Get products
  const { data: products } = useQuery({
    queryKey: ['products'],
    queryFn: () => api.getProducts(0, 100),
  })

  const { data: preselectedProduct } = useQuery({
    queryKey: ['product', preselectedProductId, 'image-freestyle-prefill'],
    queryFn: () => api.getProduct(preselectedProductId!),
    enabled: !!preselectedProductId,
  })

  const availableProducts = useMemo(() => {
    const items = products?.items || []
    if (!preselectedProduct) {
      return items
    }
    return items.some((item) => item.id === preselectedProduct.id)
      ? items
      : [preselectedProduct, ...items]
  }, [products?.items, preselectedProduct])

  const selectedProduct = useMemo(
    () => availableProducts.find((item) => item.id === productId) || null,
    [availableProducts, productId]
  )
  const productScreenshotReference = useMemo<Asset | null>(() => {
    if (!selectedProduct?.screenshot_url) {
      return null
    }

    return {
      id: `product-screenshot-${selectedProduct.id}`,
      asset_type: 'image',
      source_type: 'screenshot',
      url: selectedProduct.screenshot_url,
      download_url: null,
      file_name: 'Product Screenshot',
      description: null,
      score: null,
      is_favorited: false,
      created_at: selectedProduct.created_at,
      product_id: selectedProduct.id,
      parent_asset_id: null,
    }
  }, [selectedProduct])
  const isProductScreenshotIncluded = Boolean(productScreenshotReference) && includeProductScreenshot
  const maxManualReferenceCount = isProductScreenshotIncluded
    ? Math.max(0, MAX_PRODUCT_REFERENCE_IMAGES - 1)
    : MAX_PRODUCT_REFERENCE_IMAGES
  const referenceHelperText = isProductScreenshotIncluded
    ? `The product screenshot is included automatically. You can add up to ${maxManualReferenceCount} more reference images.`
    : selectedProduct?.screenshot_url
      ? `The product screenshot has been removed. Add up to ${MAX_PRODUCT_REFERENCE_IMAGES} product reference images.`
    : `Add up to ${MAX_PRODUCT_REFERENCE_IMAGES} product reference images.`
  const referenceAssets = useMemo(() => {
    const assets: Asset[] = []
    const seenIds = new Set<string>()

    if (productScreenshotReference && includeProductScreenshot) {
      assets.push(productScreenshotReference)
      seenIds.add(productScreenshotReference.id)
    }

    for (const asset of manualReferenceAssets) {
      if (!seenIds.has(asset.id)) {
        assets.push(asset)
        seenIds.add(asset.id)
      }
    }

    return assets
  }, [includeProductScreenshot, manualReferenceAssets, productScreenshotReference])

  useEffect(() => {
    setIncludeProductScreenshot(true)
  }, [selectedProduct?.id])

  useEffect(() => {
    if (manualReferenceAssets.length > maxManualReferenceCount) {
      setManualReferenceAssets((prev) => prev.slice(0, maxManualReferenceCount))
    }
  }, [manualReferenceAssets.length, maxManualReferenceCount])

  const shouldShowVerticalSkill = !!selectedProduct
  const selectedVerticalIdeasMetadata = useMemo(
    () => extractVerticalIdeasMetadata(selectedProduct),
    [selectedProduct]
  )
  const ecommerceAutoReferenceAssetIds = useMemo(() => {
    if (selectedProduct?.product_type !== 'ecommerce') {
      return []
    }
    const primaryReferenceAssetId =
      typeof selectedVerticalIdeasMetadata?.primary_reference_asset_id === 'string'
        ? selectedVerticalIdeasMetadata.primary_reference_asset_id
        : null
    return extractAssetIdList(selectedVerticalIdeasMetadata?.product_image_asset_ids)
      .filter((assetId) => assetId !== primaryReferenceAssetId)
      .slice(0, maxManualReferenceCount)
  }, [maxManualReferenceCount, selectedProduct?.product_type, selectedVerticalIdeasMetadata])

  useEffect(() => {
    if (preselectedProductId) {
      setProductId(preselectedProductId)
    }
  }, [preselectedProductId])

  useEffect(() => {
    initializedSkillProductIdRef.current = null
    if (!productId) {
      setVerticalSkillKey('none')
      return
    }
    if (productId === preselectedProductId && preselectedVerticalSkillKey) {
      setVerticalSkillKey(preselectedVerticalSkillKey)
      return
    }
    setVerticalSkillKey('none')
  }, [productId, preselectedProductId, preselectedVerticalSkillKey])

  const { data: verticalSkillOptions, isLoading: isVerticalSkillOptionsLoading } = useQuery({
    queryKey: ['vertical-skill-options', productId, 'image'],
    queryFn: () => api.getVerticalSkillOptions(productId!, 'image'),
    enabled: !!productId,
  })

  const { data: ecommerceAutoReferenceAssets } = useQuery({
    queryKey: ['image-freestyle-ecommerce-product-assets', productId, ecommerceAutoReferenceAssetIds],
    queryFn: () => Promise.all(ecommerceAutoReferenceAssetIds.map((assetId) => api.getAsset(assetId))),
    enabled: ecommerceAutoReferenceAssetIds.length > 0,
  })

  useEffect(() => {
    if (!productId || !shouldShowVerticalSkill) {
      setVerticalSkillKey('none')
      initializedSkillProductIdRef.current = null
      return
    }
    if (initializedSkillProductIdRef.current === productId) {
      return
    }
    if (verticalSkillOptions) {
      const preselectedOption = productId === preselectedProductId ? preselectedVerticalSkillKey : undefined
      const resolvedKey =
        preselectedOption && verticalSkillOptions.options.some((option) => option.key === preselectedOption)
          ? preselectedOption
          : verticalSkillOptions.default_skill_key || 'none'
      setVerticalSkillKey(resolvedKey)
      initializedSkillProductIdRef.current = productId
    }
  }, [productId, preselectedProductId, preselectedVerticalSkillKey, shouldShowVerticalSkill, verticalSkillOptions])

  useEffect(() => {
    setIsVerticalGuidanceOpen(false)
  }, [productId, verticalSkillKey, shouldShowVerticalSkill])

  useEffect(() => {
    if (!selectedProduct) {
      setManualReferenceAssets([])
      autoPrefilledReferenceProductIdRef.current = null
      return
    }
    if (autoPrefilledReferenceProductIdRef.current === selectedProduct.id) {
      return
    }
    if (selectedProduct.product_type !== 'ecommerce') {
      setManualReferenceAssets([])
      autoPrefilledReferenceProductIdRef.current = selectedProduct.id
      return
    }
    if (ecommerceAutoReferenceAssetIds.length === 0) {
      setManualReferenceAssets([])
      autoPrefilledReferenceProductIdRef.current = selectedProduct.id
      return
    }
    if (!ecommerceAutoReferenceAssets) {
      return
    }
    setManualReferenceAssets(ecommerceAutoReferenceAssets.slice(0, maxManualReferenceCount))
    autoPrefilledReferenceProductIdRef.current = selectedProduct.id
  }, [
    ecommerceAutoReferenceAssetIds.length,
    ecommerceAutoReferenceAssets,
    maxManualReferenceCount,
    selectedProduct,
  ])

  const selectedVerticalSkillOption = verticalSkillOptions?.options.find((option) => option.key === verticalSkillKey) || null
  const activeIdeaSource = productId && productId === preselectedProductId ? preselectedIdeaSource : undefined
  const activeIdeaTag = productId && productId === preselectedProductId ? preselectedIdeaTag : undefined
  const defaultIdeaPoolLabel = useMemo(() => {
    if (!activeIdeaSource) return ''
    return activeIdeaTag ? `${activeIdeaSource} / ${activeIdeaTag}` : activeIdeaSource
  }, [activeIdeaSource, activeIdeaTag])

  // Get credits
  const { data: subscription } = useQuery({
    queryKey: ['subscription'],
    queryFn: () => api.getSubscription(),
  })

  // Fetch ideas for auto-recommendation when product is selected and useIdeas is on
  const { data: ideasData } = useQuery({
    queryKey: ['ideas', productId, 'unused_first', activeIdeaSource, activeIdeaTag],
    queryFn: () =>
      api.getIdeas(productId!, {
        sort: 'unused_first',
        limit: 100,
        source: activeIdeaSource,
        tag: activeIdeaTag,
      }),
    enabled: !!productId && useIdeas,
  })

  const availableIdeas = useMemo(
    () => (ideasData?.items || []).filter((idea) => normalizeIdeaAdFormat(idea.ad_format) === 'image'),
    [ideasData?.items]
  )

  const isGptImageModel = model === 'gpt_image'
  const resolvedAspectRatio = isGptImageModel
    ? getAspectRatioForGptImageSize(gptImageSize)
    : aspectRatio

  // Get aspect ratios for current model
  const availableAspectRatios = ASPECT_RATIOS_BY_MODEL[model] || ASPECT_RATIOS_BY_MODEL.gemini_image
  // Reset aspect ratio when model changes
  useEffect(() => {
    if (isGptImageModel) {
      return
    }
    const supportedValues = availableAspectRatios.map(ar => ar.value)
    if (!supportedValues.includes(aspectRatio)) {
      setAspectRatio('1:1')
    }
  }, [model, availableAspectRatios, aspectRatio, isGptImageModel])

  const handleModelChange = (nextModel: ToolId) => {
    if (nextModel === 'gpt_image' && model !== 'gpt_image') {
      setGptImageSize(getDefaultGptImageSizeForAspectRatio(aspectRatio))
    }
    setModel(nextModel)
  }

  const appendManualReferenceAssets = useCallback((incomingAssets: Asset[]) => {
    let shouldIncludeCurrentProductScreenshot = false
    const selectedProductId = selectedProduct?.id

    setManualReferenceAssets((prev) => {
      const next = [...prev]
      const seenIds = new Set(prev.map((asset) => asset.id))

      for (const asset of incomingAssets) {
        const isCurrentProductScreenshot =
          Boolean(selectedProductId) &&
          asset.source_type === 'screenshot' &&
          asset.product_id === selectedProductId

        if (isCurrentProductScreenshot) {
          shouldIncludeCurrentProductScreenshot = true
          continue
        }

        if (seenIds.has(asset.id) || next.length >= maxManualReferenceCount) {
          continue
        }

        next.push(asset)
        seenIds.add(asset.id)
      }

      return next
    })

    if (shouldIncludeCurrentProductScreenshot) {
      setIncludeProductScreenshot(true)
    }
  }, [maxManualReferenceCount, selectedProduct?.id])

  // Auto-fill slots with recommended ideas when count or useIdeas changes
  const autoFillSlots = useCallback((newCount: number, currentSlots: SlotItem[], ideas: Idea[]) => {
    if (!useIdeas) {
      // When ideas off, all slots are null (freestyle)
      return Array(newCount).fill(null)
    }

    const newSlots: SlotItem[] = []
    const usedIdeaIds = new Set<string>()

    // Preserve existing idea slots up to newCount
    for (let i = 0; i < Math.min(newCount, currentSlots.length); i++) {
      const slot = currentSlots[i]
      if (slot) {
        newSlots.push(slot)
        usedIdeaIds.add(slot.idea.id)
      } else {
        newSlots.push(null) // placeholder, will be filled below
      }
    }

    // If we need more slots
    while (newSlots.length < newCount) {
      newSlots.push(null)
    }

    // Fill null slots with recommended ideas
    const unusedIdeas = ideas.filter(idea => !usedIdeaIds.has(idea.id))
    let ideaIdx = 0
    for (let i = 0; i < newSlots.length; i++) {
      if (!newSlots[i] && ideaIdx < unusedIdeas.length) {
        const idea = unusedIdeas[ideaIdx]
        newSlots[i] = createIdeaSlot(idea)
        usedIdeaIds.add(idea.id)
        ideaIdx++
      }
    }

    return newSlots
  }, [useIdeas])

  // Re-fill slots when count, useIdeas, or available ideas change
  useEffect(() => {
    setSlots(prev => autoFillSlots(count, prev, availableIdeas))
  }, [count, useIdeas, availableIdeas, autoFillSlots])

  // Count spinner handlers
  const handleCountChange = (newCount: number) => {
    const clamped = Math.max(1, Math.min(MAX_GENERATION_COUNT, newCount))
    setCount(clamped)
    // Keep activeSlotIndex in bounds
    setActiveSlotIndex(prev => Math.min(prev, clamped - 1))
  }

  // Track pending task ID
  const pendingTaskIdRef = useRef<string | null>(null)

  const onMutationSuccess = (data: { id: string }) => {
    if (pendingTaskIdRef.current) {
      removeTask(pendingTaskIdRef.current)
      pendingTaskIdRef.current = null
    }
    addTask({
      id: data.id,
      type: 'image_freestyle',
      status: 'processing',
      metadata: {
        count,
        aspectRatio: resolvedAspectRatio,
        imageSize: isGptImageModel ? gptImageSize : undefined,
        imageQuality: isGptImageModel ? gptImageQuality : undefined,
        model,
        productId,
        useIdeas,
      },
    })
    startPolling(data.id)
  }

  const onMutationError = (error: any) => {
    if (pendingTaskIdRef.current) {
      updateTask(pendingTaskIdRef.current, {
        status: 'failed',
        error: error?.response?.data?.error?.message || error?.response?.data?.detail || 'Failed to start generation',
      })
      pendingTaskIdRef.current = null
    }
  }

  const resolvedReferenceAssetIds = manualReferenceAssets.map((asset) => asset.id)
  const resolvedReferenceAssetId = resolvedReferenceAssetIds[0]

  // Unified slot mutation (Idea slots + Freestyle null slots)
  const generateMutation = useMutation({
    mutationFn: () => {
      const apiSlots: GenerationSlot[] = slots.map(slot =>
        slot ? { idea_id: slot.idea.id, creative_reference_url: slot.ideaInput.creative_reference_url || null } : null
      )
      return api.generateImage({
        slots: apiSlots,
        prompt,
        model,
        count,
        aspect_ratio: resolvedAspectRatio,
        image_size: isGptImageModel ? gptImageSize : undefined,
        image_quality: isGptImageModel ? gptImageQuality : undefined,
        product_id: productId,
        include_product_screenshot: includeProductScreenshot,
        reference_asset_id: resolvedReferenceAssetId,
        reference_asset_ids: resolvedReferenceAssetIds,
        vertical_skill_key: productId ? verticalSkillKey : undefined,
        requirement_id: embedRequirementId ?? undefined,
      })
    },
    onSuccess: onMutationSuccess,
    onError: onMutationError,
  })

  const handleGenerate = () => {
    if (currentTask && (currentTask.status === 'completed' || currentTask.status === 'failed')) {
      removeTask(currentTask.id)
    }

    const pendingId = `pending-${Date.now()}`
    pendingTaskIdRef.current = pendingId
    addTask({
      id: pendingId,
      type: 'image_freestyle',
      status: 'pending',
      metadata: {
        count,
        aspectRatio: resolvedAspectRatio,
        imageSize: isGptImageModel ? gptImageSize : undefined,
        imageQuality: isGptImageModel ? gptImageQuality : undefined,
        model,
        productId,
        useIdeas,
      },
    })

    generateMutation.mutate()
  }

  // Slot operations
  const handleRemoveIdea = (index: number) => {
    setSlots(prev => {
      const next = [...prev]
      // Replace with next available recommended idea
      const usedIds = new Set(next.filter(s => s !== null).map(s => s!.idea.id))
      // Remove the idea at this index first
      if (next[index]) {
        usedIds.delete(next[index]!.idea.id)
      }
      const replacement = availableIdeas.find(idea => !usedIds.has(idea.id))
      if (replacement) {
        next[index] = createIdeaSlot(replacement)
      } else {
        next[index] = null
      }
      return next
    })
  }

  const handleReplaceIdea = (index: number) => {
    setReplacingSlotIndex(index)
    setShowImportModal(true)
  }

  const handleAddIdeaToSlot = (index: number) => {
    setReplacingSlotIndex(index)
    setShowImportModal(true)
  }

  const handleCreativeRefChange = (index: number, url: string | null) => {
    setSlots(prev =>
      prev.map((slot, i) =>
        i === index && slot
          ? { ...slot, ideaInput: { ...slot.ideaInput, creative_reference_url: url } }
          : slot
      )
    )
  }

  // Count idea vs freestyle slots
  const ideaSlotCount = slots.filter(s => s !== null).length
  const freestyleSlotCount = slots.filter(s => s === null).length

  const creditsNeeded = count
  const hasEnoughCredits = (subscription?.credits_available || 0) >= creditsNeeded

  // Existing idea IDs in slots (for excluding in modal)
  const existingIdeaIds = slots.filter(s => s !== null).map(s => s!.idea.id)
  const replaceableIdeaId =
    replacingSlotIndex !== null && slots[replacingSlotIndex]
      ? slots[replacingSlotIndex]!.idea.id
      : null
  const importExcludedIdeaIds = replaceableIdeaId
    ? existingIdeaIds.filter((ideaId) => ideaId !== replaceableIdeaId)
    : existingIdeaIds
  const maxImportSelection = useMemo(() => {
    const occupiedIdeaSlots = slots.filter((slot) => slot !== null).length
    const availableSlotCount =
      count -
      (occupiedIdeaSlots - (replacingSlotIndex !== null && slots[replacingSlotIndex] ? 1 : 0))
    return Math.max(1, availableSlotCount)
  }, [count, replacingSlotIndex, slots])

  const handleImageDownload = async (image: Asset) => {
    try {
      const result = await api.recordAssetDownload(image.id)
      if (!result.download_url) {
        toast({ title: 'Download unavailable', variant: 'destructive' })
        return
      }
      downloadFile(result.download_url)
    } catch {
      toast({ title: 'Download failed', variant: 'destructive' })
    }
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Generation Form */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
            {/* Product Association (shared) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Associate with Product <span className="text-gray-400">(Optional)</span>
              </label>
              <select
                value={productId || ''}
                onChange={(e) => setProductId(e.target.value || undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                disabled={isTaskRunning}
              >
                <option value="">No product</option>
                {availableProducts.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.product_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Product Reference Image (shared) */}
            <ReferenceImageSelector
              selectedAssets={referenceAssets}
              onSelect={() => setShowAssetPicker(true)}
              onClearAsset={(asset) => {
                if (asset.id.startsWith('product-screenshot-')) {
                  setIncludeProductScreenshot(false)
                  return
                }
                setManualReferenceAssets((prev) => prev.filter((item) => item.id !== asset.id))
              }}
              canClearAsset={() => true}
              addButtonLabel="Add reference"
              canAddMore={manualReferenceAssets.length < maxManualReferenceCount}
              disabled={isTaskRunning}
              emptyTitle="Add product references"
              emptyDescription={referenceHelperText}
              helperText={referenceHelperText}
              showLabel={false}
              showSourceLabel={false}
              multiSelectLayout="horizontal"
            />

            {/* Count Spinner + Use Ideas Toggle */}
            <div className="flex items-center gap-4">
              {/* Count Spinner */}
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Number of Images
                </label>
                <div className="inline-flex items-stretch">
                  <button
                    type="button"
                    onClick={() => handleCountChange(count - 1)}
                    disabled={isTaskRunning || count <= 1}
                    className="flex items-center justify-center w-9 h-9 border border-gray-300 rounded-l-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </button>
                  <input
                    type="number"
                    min={1}
                    max={10}
                    value={count}
                    onChange={(e) => handleCountChange(parseInt(e.target.value) || 1)}
                    disabled={isTaskRunning}
                    className="w-12 h-9 text-center text-sm border-t border-b border-gray-300 focus:outline-none focus:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <button
                    type="button"
                    onClick={() => handleCountChange(count + 1)}
                    disabled={isTaskRunning || count >= 10}
                    className="flex items-center justify-center w-9 h-9 border border-gray-300 rounded-r-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronUp className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Use Ideas Toggle */}
              <div className="flex min-w-[96px] flex-col items-center justify-end pl-2">
                <label className="mb-1.5 flex items-center justify-center gap-1 text-center text-sm font-medium text-gray-700">
                  Use Ideas
                  <span className="relative group">
                    <HelpCircle className="h-3.5 w-3.5 text-gray-400 cursor-help" />
                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover:block w-56 px-2.5 py-1.5 text-xs text-white bg-gray-800 rounded-md shadow-lg z-50 font-normal leading-relaxed">
                      When enabled, slots are auto-filled with recommended Ideas from your product's idea library. Each Idea provides creative direction and optional style references.
                    </span>
                  </span>
                </label>
                <button
                  type="button"
                  role="switch"
                  aria-checked={useIdeas}
                  onClick={() => setUseIdeas(!useIdeas)}
                  disabled={isTaskRunning}
                  className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                    useIdeas ? 'bg-primary' : 'bg-gray-200'
                  } ${isTaskRunning ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <span
                    className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform shadow-sm ${
                      useIdeas ? 'translate-x-7' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            {useIdeas && defaultIdeaPoolLabel && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
                Default idea pool: {defaultIdeaPoolLabel}
              </div>
            )}

            {/* Slot Carousel (only when Use Ideas is ON) */}
            {useIdeas && (
              <div>
                {/* Navigation header */}
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-gray-500">
                    {count} image{count !== 1 ? 's' : ''}
                    {ideaSlotCount > 0 && `: ${ideaSlotCount} with Ideas`}
                    {freestyleSlotCount > 0 && `${ideaSlotCount > 0 ? ' + ' : ': '}${freestyleSlotCount} Freestyle`}
                    {freestyleSlotCount > 0 && availableIdeas.length === 0 && productId && (
                      <span className="text-amber-600"> — Not enough ideas</span>
                    )}
                  </p>
                  {count > 1 && (
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setActiveSlotIndex(prev => Math.max(0, prev - 1))}
                        disabled={activeSlotIndex === 0}
                        className="p-0.5 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      <span className="text-xs text-gray-500 tabular-nums w-10 text-center">
                        {activeSlotIndex + 1} / {count}
                      </span>
                      <button
                        type="button"
                        onClick={() => setActiveSlotIndex(prev => Math.min(count - 1, prev + 1))}
                        disabled={activeSlotIndex === count - 1}
                        className="p-0.5 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Active slot card */}
                {(() => {
                  const slot = slots[activeSlotIndex]
                  const index = activeSlotIndex
                  return slot ? (
                    /* Idea Slot */
                    <div className="bg-white rounded-lg border border-gray-200 p-3">
                      <div className="flex items-start justify-between gap-2">
                        <button
                          type="button"
                          onClick={() => setDetailIdea(slot.idea)}
                          className="flex-1 min-w-0 text-left rounded hover:bg-gray-50 p-0.5 -m-0.5 transition-colors"
                        >
                          <div className="flex items-center gap-1.5">
                            <Lightbulb className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                            <p className="font-medium text-sm truncate">{slot.idea.title}</p>
                            {slot.idea.used_count === 0 ? (
                              <span className="text-xs text-green-600 bg-green-50 px-1.5 py-0.5 rounded flex-shrink-0">New</span>
                            ) : (
                              <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded flex-shrink-0">Used {slot.idea.used_count}x</span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-1 line-clamp-2">{slot.idea.description}</p>
                        </button>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={() => handleReplaceIdea(index)}
                            disabled={isTaskRunning}
                            className="p-1 text-gray-400 hover:text-primary hover:bg-primary/5 rounded transition-colors"
                            title="Replace idea"
                          >
                            <ArrowRightLeft className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => handleRemoveIdea(index)}
                            disabled={isTaskRunning}
                            className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                            title="Remove idea"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>

                      {/* Creative Reference Selection */}
                      {filterValidMediaUrls(slot.idea.media_urls).length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-gray-600 mb-1.5">Creative Ref (optional):</p>
                          <div className="flex gap-2 flex-wrap items-center">
                            {filterValidMediaUrls(slot.idea.media_urls).map((url, idx) => (
                              <button
                                key={idx}
                                onClick={() => handleCreativeRefChange(index, url)}
                                className={`relative rounded overflow-hidden border-2 transition-colors ${
                                  slot.ideaInput.creative_reference_url === url
                                    ? 'border-primary'
                                    : 'border-gray-200 hover:border-gray-300'
                                }`}
                              >
                                <img src={url} alt={`Ref ${idx + 1}`} className="w-10 h-10 object-cover" />
                              </button>
                            ))}
                            {slot.ideaInput.creative_reference_url && (
                              <button
                                type="button"
                                onClick={() => handleCreativeRefChange(index, null)}
                                className="text-xs text-gray-500 hover:text-gray-700 underline underline-offset-2"
                              >
                                Clear
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Freestyle Slot */
                    <div className="bg-gray-50 rounded-lg border border-dashed border-gray-300 p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5 text-gray-400">
                          <span className="text-xs">Freestyle — uses prompt only</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleAddIdeaToSlot(index)}
                          disabled={isTaskRunning}
                          className="text-xs text-primary hover:text-primary/80 flex items-center gap-0.5"
                        >
                          <Plus className="h-3 w-3" />
                          Add Idea
                        </button>
                      </div>
                    </div>
                  )
                })()}

                {/* Dot indicators for quick navigation */}
                {count > 1 && (
                  <div className="flex items-center justify-center gap-1 mt-2">
                    {slots.map((slot, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setActiveSlotIndex(i)}
                        className={`rounded-full transition-colors ${
                          i === activeSlotIndex
                            ? 'w-2 h-2 bg-primary'
                            : slot
                              ? 'w-1.5 h-1.5 bg-amber-300 hover:bg-amber-400'
                              : 'w-1.5 h-1.5 bg-gray-300 hover:bg-gray-400'
                        }`}
                        title={`Slot ${i + 1}${slot ? `: ${slot.idea.title}` : ' (Freestyle)'}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Prompt */}
            <div>
              {shouldShowVerticalSkill && (
                <div className="mb-4 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Vertical Guidance
                    </label>
                    <select
                      value={verticalSkillKey}
                      onChange={(e) => setVerticalSkillKey(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm"
                      disabled={isTaskRunning || isVerticalSkillOptionsLoading}
                    >
                      {isVerticalSkillOptionsLoading && <option value="none">Loading skills...</option>}
                      {!isVerticalSkillOptionsLoading && verticalSkillOptions?.options.map((option) => (
                        <option key={option.key} value={option.key}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3">
                    {selectedVerticalSkillOption?.content ? (
                      <div className="space-y-3">
                        <button
                          type="button"
                          onClick={() => setIsVerticalGuidanceOpen((open) => !open)}
                          className="flex w-full items-center justify-between rounded-md border border-gray-200 bg-white px-3 py-2 text-left text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100"
                        >
                          <span>{isVerticalGuidanceOpen ? 'Hide Guidance' : 'View Guidance'}</span>
                          {isVerticalGuidanceOpen ? (
                            <ChevronUp className="h-4 w-4 text-gray-500" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-gray-500" />
                          )}
                        </button>
                        {isVerticalGuidanceOpen && (
                          <div className="max-h-72 overflow-y-auto rounded-md border border-gray-200 bg-white px-4 py-3 shadow-sm">
                            <MarkdownArticle
                              markdown={selectedVerticalSkillOption.content}
                              className="text-sm"
                            />
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500">
                        No industry guidance is applied for this run.
                      </p>
                    )}
                  </div>
                </div>
              )}

              <label className="block text-sm font-medium text-gray-700 mb-2">
                {useIdeas ? (
                  <>Prompt <span className="text-gray-400">(applied to all / used by Freestyle slots)</span></>
                ) : (
                  <>User Preference <span className="text-gray-400">(Optional)</span></>
                )}
              </label>
              <textarea
                rows={3}
                placeholder={useIdeas
                  ? 'Add extra style or content guidance for all slots...'
                  : 'Describe the image style or content you prefer...'
                }
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                disabled={isTaskRunning}
              />
            </div>

            {/* Model */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Model</label>
              <div className="space-y-2">
                {MODELS.map((m) => (
                  <label
                    key={m.value}
                    className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                      model === m.value
                        ? 'border-primary bg-primary/5'
                        : 'border-gray-200 hover:border-gray-300'
                    } ${isTaskRunning ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <input
                      type="radio"
                      name="model"
                      value={m.value}
                      checked={model === m.value}
                      onChange={(e) => handleModelChange(e.target.value as ToolId)}
                      className="sr-only"
                      disabled={isTaskRunning}
                    />
                    <div>
                      <p className="font-medium">{m.label}</p>
                      <p className="text-sm text-gray-500">{m.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Size */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Size</label>
              {isGptImageModel ? (
                <div className="grid grid-cols-2 gap-2">
                  {GPT_IMAGE_SIZE_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setGptImageSize(option.value)}
                      disabled={isTaskRunning}
                      className={`rounded-lg border px-3 py-2 text-left transition-colors ${
                        gptImageSize === option.value
                          ? 'border-primary bg-primary/5 text-primary'
                          : 'border-gray-200 hover:border-gray-300'
                      } ${isTaskRunning ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <div className="text-xs font-medium">{option.label}</div>
                      <div className="text-[11px] text-gray-500">{option.description}</div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {availableAspectRatios.map((ar) => (
                    <button
                      key={ar.value}
                      type="button"
                      onClick={() => setAspectRatio(ar.value)}
                      disabled={isTaskRunning}
                      className={`px-2 py-2 text-xs border rounded-lg transition-colors text-center truncate ${
                        aspectRatio === ar.value
                          ? 'border-primary bg-primary/5 text-primary'
                          : 'border-gray-200 hover:border-gray-300'
                      } ${isTaskRunning ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {ar.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {isGptImageModel && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Quality</label>
                <div className="grid grid-cols-2 gap-2">
                  {GPT_IMAGE_QUALITY_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setGptImageQuality(option.value)}
                      disabled={isTaskRunning}
                      className={`rounded-lg border px-3 py-2 text-left transition-colors ${
                        gptImageQuality === option.value
                          ? 'border-primary bg-primary/5 text-primary'
                          : 'border-gray-200 hover:border-gray-300'
                      } ${isTaskRunning ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <div className="text-sm font-medium">{option.label}</div>
                      <div className="text-xs text-gray-500">{option.description}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Generate Button */}
            <Button
              onClick={handleGenerate}
              disabled={
                !hasEnoughCredits ||
                generateMutation.isPending ||
                isTaskRunning
              }
              className="w-full"
            >
              {generateMutation.isPending || isTaskRunning ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Wand2 className="w-4 h-4 mr-2" />
              )}
              Generate {count} ({creditsNeeded} Credit{creditsNeeded !== 1 ? 's' : ''})
            </Button>

            {!hasEnoughCredits && (
              <p className="text-red-500 text-sm text-center">
                Not enough credits. You have {subscription?.credits_available || 0} credits.
              </p>
            )}
          </div>
        </div>

        {/* Right: Results */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold mb-4">Generated Images</h2>

            {isTaskRunning ? (
              <div className="flex flex-col items-center justify-center py-16 gap-4">
                <Loader2 className="w-12 h-12 animate-spin text-primary" />
                <div className="text-center">
                  <p className="text-gray-700 font-medium">Generating your images...</p>
                  <p className="text-sm text-gray-500">This may take 20-120 seconds</p>
                </div>
                <div className="w-full max-w-xs bg-gray-200 rounded-full h-2 overflow-hidden">
                  <div className="bg-primary h-full rounded-full animate-pulse" style={{ width: '60%' }} />
                </div>
              </div>
            ) : taskStatus === 'failed' ? (
              <div className="flex flex-col items-center justify-center py-16 gap-4">
                <AlertCircle className="w-12 h-12 text-red-500" />
                <div className="text-center">
                  <p className="text-red-700 font-medium mb-2">Generation Failed</p>
                  <p className="text-sm text-red-600">{taskError || 'An error occurred during image generation'}</p>
                  <Button
                    onClick={handleGenerate}
                    variant="outline"
                    size="sm"
                    className="mt-4"
                    disabled={!hasEnoughCredits}
                  >
                    Try Again
                  </Button>
                </div>
              </div>
            ) : generatedImages.length > 0 ? (
              <div className="grid grid-cols-2 gap-4">
                {generatedImages.map((image, idx) => (
                  <div
                    key={image.id}
                    className="relative group rounded-lg overflow-hidden border border-gray-200 cursor-pointer bg-gray-100"
                    onClick={() => setPreviewImage(image)}
                  >
                    <div className="aspect-square flex items-center justify-center">
                      <img
                        src={image.url || ''}
                        alt={`Generated ${idx + 1}`}
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                    {image.idea_title && (
                      <div className="absolute top-2 left-2 px-2 py-1 bg-black/60 rounded text-white text-xs font-medium max-w-[75%] truncate">
                        Idea · {image.idea_title}
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); setPreviewImage(image); }}
                        className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                      >
                        <Maximize2 className="w-4 h-4 text-gray-700" />
                      </button>
                      {image.url && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            void handleImageDownload(image)
                          }}
                          className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                        >
                          <Download className="w-4 h-4 text-gray-700" />
                        </button>
                      )}
                      <button
                        onClick={(e) => e.stopPropagation()}
                        className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                      >
                        <Star className="w-4 h-4 text-gray-700" />
                      </button>
                    </div>
                    {image.score && (
                      <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/50 rounded text-white text-sm">
                        Score: {image.score.toFixed(1)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <Image className="w-16 h-16 mb-4" />
                <p>Your generated images will appear here</p>
              </div>
            )}

            {generatedImages.length > 0 && taskStatus === 'completed' && (
              <div className="mt-4 flex justify-center">
                <Button variant="outline" onClick={handleGenerate} disabled={!hasEnoughCredits}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Regenerate
                </Button>
              </div>
            )}
          </div>

          {/* Image Generation Info */}
          <div className="mt-6 bg-blue-50 rounded-xl p-4">
            <h3 className="font-medium text-blue-900 mb-2">About Image Generation</h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>- Image generation typically takes 20-120 seconds</li>
              <li>- More images require more credits</li>
              <li>- Results are automatically saved to your asset library</li>
              {referenceAssets.length > 0 && (
                <li>- Product reference images preserve product/brand consistency across angles and details</li>
              )}
              {useIdeas && ideaSlotCount > 0 && (
                <li>- Ideas provide creative direction; creative refs add style inspiration</li>
              )}
            </ul>
          </div>
        </div>
      </div>

      {/* Asset Picker Modal */}
      <AssetPickerModal
        open={showAssetPicker}
        onClose={() => setShowAssetPicker(false)}
        onSelect={(asset) => appendManualReferenceAssets([asset])}
        onSelectMany={appendManualReferenceAssets}
        title="Add Product Reference Images"
        filterProductId={productId}
        assetType="image"
        requirementId={embedRequirementId ?? undefined}
      />

      {/* Import Idea Modal */}
      <ImportIdeaModal
        open={showImportModal}
        onClose={() => {
          setShowImportModal(false)
          setReplacingSlotIndex(null)
        }}
        defaultProductId={productId}
        defaultTag={activeIdeaTag}
        onConfirm={(ideas) => {
          if (ideas.length === 0) return
          setSlots((prev) => assignImportedIdeasToSlots(prev, ideas, replacingSlotIndex))
          setReplacingSlotIndex(null)
        }}
        maxSelection={maxImportSelection}
        excludeIds={importExcludedIdeaIds}
        preferredAdFormat="image"
      />

      {/* Image Preview Modal */}
      {previewImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={() => setPreviewImage(null)}
        >
          <button
            onClick={() => setPreviewImage(null)}
            className="absolute top-4 right-4 p-2 text-white hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="h-6 w-6" />
          </button>

          <div
            className="max-w-[90vw] max-h-[90vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            {previewImage.url && (
              <img
                src={previewImage.url}
                alt="Preview"
                className="max-w-full max-h-[90vh] rounded-lg object-contain"
              />
            )}
          </div>

          {previewImage.url && (
            <button
              type="button"
              className="absolute bottom-4 right-4 px-4 py-2 bg-white rounded-lg flex items-center gap-2 hover:bg-gray-100 transition-colors"
              onClick={(e) => {
                e.stopPropagation()
                void handleImageDownload(previewImage)
              }}
            >
              <Download className="h-4 w-4" />
              Download
            </button>
          )}
        </div>
      )}

      <IdeaDetailModal
        idea={detailIdea}
        open={!!detailIdea}
        onClose={() => setDetailIdea(null)}
        readOnly
      />
    </div>
  )
}
